import React, { useEffect, useRef, useState } from 'react';
import { getMergeSortAnimations } from '../algorithms/mergeSort';
import { getBubbleSortAnimations } from '../algorithms/bubbleSort';
import { getQuickSortAnimations } from '../algorithms/quickSort';

const ALGORITHM_COLORS = {
  merge: 'bg-yellow-500',
  bubble: 'bg-purple-500',
  quick: 'bg-pink-500',
};

const BAR_COLORS = {
  merge: '#facc15',   // yellow-500
  bubble: '#a855f7',  // purple-500
  quick: '#ec4899',   // pink-500
};

const SortingVisualizer = ({ algorithm }) => {
  const [array, setArray] = useState([]);
  const [isSorting, setIsSorting] = useState(false);
  const stopRef = useRef(false); // NEW: Track stop request
  const arrayRef = useRef([]);

  const generateArray = () => {
    if (isSorting) return;
    const newArr = Array.from({ length: 60 }, () => Math.floor(Math.random() * 300) + 20);
    setArray(newArr);
  };

  useEffect(() => {
    generateArray();
  }, []);

  const getAnimations = () => {
    switch (algorithm) {
      case 'merge':
        return getMergeSortAnimations([...array]);
      case 'bubble':
        return getBubbleSortAnimations([...array]);
      case 'quick':
        return getQuickSortAnimations([...array]);
      default:
        return [];
    }
  };

  const getColor = () => BAR_COLORS[algorithm] || '#38bdf8'; // default blue
  const getTailwindColor = () => ALGORITHM_COLORS[algorithm] || 'bg-sky-400';

  const runSort = async () => {
    if (isSorting) return;

    const animations = getAnimations();
    setIsSorting(true);
    stopRef.current = false;

    for (let idx = 0; idx < animations.length; idx++) {
      if (stopRef.current) break;

      const [i, j, type] = animations[idx];
      const barI = arrayRef.current[i];
      const barJ = arrayRef.current[j];

      if (!barI || !barJ) continue;

      if (type === 'compare') {
        barI.style.backgroundColor = 'red';
        barJ.style.backgroundColor = 'red';
      } else if (type === 'swap') {
        const temp = barI.style.height;
        barI.style.height = barJ.style.height;
        barJ.style.height = temp;

        barI.style.backgroundColor = 'green';
        barJ.style.backgroundColor = 'green';
      } else if (type === 'overwrite') {
        barI.style.height = `${j}px`;
        barI.style.backgroundColor = 'green';
      }

      await new Promise((resolve) => setTimeout(resolve, 30));

      barI.style.backgroundColor = getColor();
      barJ.style.backgroundColor = getColor();
    }

    setIsSorting(false);
    stopRef.current = false;
  };

  const handleStop = () => {
    stopRef.current = true;
    setIsSorting(false);
  };

  return (
    <div className="p-6 flex flex-col items-center">
      {/* Bar Container */}
      <div className="flex justify-center items-end h-[70vh] space-x-1">
        {array.map((value, idx) => (
          <div
            key={idx}
            ref={(el) => (arrayRef.current[idx] = el)}
            className={`bar ${getTailwindColor()} rounded-sm transition-all duration-75`}
            style={{
              height: `${value}px`,
              width: '8px',
            }}
          ></div>
        ))}
      </div>

      {/* Control Buttons */}
      <div className="mt-6 flex gap-4">
        <button
          onClick={generateArray}
          className={`px-4 py-2 rounded font-medium ${
            isSorting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white'
          }`}
          disabled={isSorting}
        >
          New Array
        </button>

        <button
          onClick={runSort}
          className={`px-4 py-2 rounded font-medium ${
            isSorting ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 text-white'
          }`}
          disabled={isSorting}
        >
          Sort
        </button>

        <button
          onClick={handleStop}
          className={`px-4 py-2 rounded font-medium ${
            isSorting ? 'bg-red-600 text-white' : 'bg-gray-400 cursor-not-allowed'
          }`}
          disabled={!isSorting}
        >
          Stop
        </button>
      </div>
    </div>
  );
};

export default SortingVisualizer;
